import themeConfig from "@/core/configs/theme.config";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

const THEME_CONFIG_STORAGE_PREFIX = "themeConfig";
export const DEFAULT_THEME_CONFIG_STORAGE_KEY = `${THEME_CONFIG_STORAGE_PREFIX}:default`;

export type PersistedThemeConfigKey =
  | "theme"
  | "menu"
  | "layout"
  | "rtlClass"
  | "animation"
  | "navbar"
  | "semidark";

type ThemeConfigScopedPayload<T> = {
  value: T;
  storageKey?: string;
  persist?: boolean;
};

const initialState = {
  isDarkMode: false,
  sidebar: false,
  theme: themeConfig.theme,
  menu: themeConfig.menu,
  layout: themeConfig.layout,
  rtlClass: themeConfig.rtlClass,
  animation: themeConfig.animation,
  navbar: themeConfig.navbar,
  locale: themeConfig.locale,
  semidark: themeConfig.semidark,
  languageList: [
    { code: "zh", name: "Chinese" },
    { code: "da", name: "Danish" },
    { code: "en", name: "English" },
    { code: "fr", name: "French" },
    { code: "de", name: "German" },
    { code: "el", name: "Greek" },
    { code: "hu", name: "Hungarian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "ru", name: "Russian" },
    { code: "es", name: "Spanish" },
    { code: "sv", name: "Swedish" },
    { code: "tr", name: "Turkish" },
    { code: "ae", name: "Arabic" },
  ],
};

type HydrateThemeConfigPayload = Partial<
  Omit<typeof initialState, "semidark">
> & {
  semidark?: boolean | string;
  storageKey?: string;
  persist?: boolean;
};

const persistedThemeConfigKeys: PersistedThemeConfigKey[] = [
  "theme",
  "menu",
  "layout",
  "rtlClass",
  "animation",
  "navbar",
  "semidark",
];

export function createThemeConfigStorageKey({
  moduleId,
  roleModuleId,
}: {
  moduleId?: number | string | null;
  roleModuleId?: number | string | null;
} = {}) {
  if (!moduleId || !roleModuleId) return DEFAULT_THEME_CONFIG_STORAGE_KEY;

  return `${THEME_CONFIG_STORAGE_PREFIX}:module:${moduleId}:role:${roleModuleId}`;
}

export function getThemeConfigStorageItem(
  key: PersistedThemeConfigKey,
  storageKey?: string
) {
  if (typeof window === "undefined") return null;

  // Theme (dark mode) is stored globally, not per-module
  const actualStorageKey = key === "theme" ? undefined : storageKey;

  return localStorage.getItem(actualStorageKey ? `${actualStorageKey}:${key}` : key);
}

function setThemeConfigStorageItem(
  key: PersistedThemeConfigKey,
  value: unknown,
  storageKey?: string,
  persist = true
) {
  if (typeof window === "undefined" || !persist) return;

  // Theme (dark mode) is stored globally, not per-module
  const actualStorageKey = key === "theme" ? undefined : storageKey;

  localStorage.setItem(
    actualStorageKey ? `${actualStorageKey}:${key}` : key,
    String(value)
  );
}

function isScopedPayload<T>(
  payload: T | ThemeConfigScopedPayload<T>
): payload is ThemeConfigScopedPayload<T> {
  return (
    typeof payload === "object" &&
    payload !== null &&
    ("value" in payload || "storageKey" in payload || "persist" in payload)
  );
}

function resolveThemeConfigPayload<T>(
  payload: T | ThemeConfigScopedPayload<T> | undefined,
  fallback: T
) {
  if (payload !== undefined && isScopedPayload(payload)) {
    return {
      value: payload.value ?? fallback,
      storageKey: payload.storageKey,
      persist: payload.persist !== false,
    };
  }

  return {
    value: payload ?? fallback,
    storageKey: undefined,
    persist: true,
  };
}

function persistThemeConfigState(
  state: any,
  storageKey?: string,
  persist = true
) {
  persistedThemeConfigKeys.forEach((key) => {
    setThemeConfigStorageItem(key, state[key], storageKey, persist);
  });
}

const themeConfigSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    hydrateThemeConfig(
      state,
      { payload }: PayloadAction<HydrateThemeConfigPayload>
    ) {
      const { storageKey, persist = true, ...configPayload } = payload;

      Object.entries(configPayload).forEach(([key, value]) => {
        if (value !== undefined) {
          if (key === "semidark") {
            state.semidark = value === true || value === "true";
          } else {
            (state as any)[key] = value;
          }
        }
      });
      if (
        state.theme === "dark" ||
        (state.theme === "system" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches)
      ) {
        state.isDarkMode = true;
        document.body.classList.add("dark");
      } else {
        state.isDarkMode = false;
        document.body.classList.remove("dark");
      }

      document
        .querySelector("html")
        ?.setAttribute("dir", state.rtlClass || "ltr");

      persistThemeConfigState(state, storageKey, persist);
    },
    toggleTheme(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.theme);
      payload = resolved.value; // light | dark | system
      setThemeConfigStorageItem(
        "theme",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.theme = payload;
      if (payload === "light") {
        state.isDarkMode = false;
      } else if (payload === "dark") {
        state.isDarkMode = true;
      } else if (payload === "system") {
        if (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
          state.isDarkMode = true;
        } else {
          state.isDarkMode = false;
        }
      }

      if (state.isDarkMode) {
        document.querySelector("body")?.classList.add("dark");
      } else {
        document.querySelector("body")?.classList.remove("dark");
      }
    },
    toggleMenu(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.menu);
      payload = resolved.value; // vertical, collapsible-vertical, horizontal
      setThemeConfigStorageItem(
        "menu",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.menu = payload;
    },
    toggleLayout(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.layout);
      payload = resolved.value; // full, boxed-layout
      setThemeConfigStorageItem(
        "layout",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.layout = payload;
    },
    toggleRTL(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.rtlClass);
      payload = resolved.value; // rtl, ltr
      setThemeConfigStorageItem(
        "rtlClass",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.rtlClass = payload;
      document
        .querySelector("html")
        ?.setAttribute("dir", state.rtlClass || "ltr");
    },
    toggleAnimation(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.animation);
      payload = resolved.value; // animate__fadeIn, animate__fadeInDown, animate__fadeInUp, animate__fadeInLeft, animate__fadeInRight, animate__slideInDown, animate__slideInLeft, animate__slideInRight, animate__zoomIn
      payload = payload?.trim();
      setThemeConfigStorageItem(
        "animation",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.animation = payload;
    },
    toggleNavbar(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.navbar);
      payload = resolved.value; // navbar-sticky, navbar-floating, navbar-static
      setThemeConfigStorageItem(
        "navbar",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.navbar = payload;
    },
    toggleSemidark(state, { payload }) {
      const resolved = resolveThemeConfigPayload(payload, state.semidark);
      payload = resolved.value === true || String(resolved.value) === "true";
      setThemeConfigStorageItem(
        "semidark",
        payload,
        resolved.storageKey,
        resolved.persist
      );
      state.semidark = payload;
    },
    toggleSidebar(state) {
      state.sidebar = !state.sidebar;
    },
    resetToggleSidebar(state) {
      state.sidebar = false;
    },
  },
});

export const {
  hydrateThemeConfig,
  toggleTheme,
  toggleMenu,
  toggleLayout,
  toggleRTL,
  toggleAnimation,
  toggleNavbar,
  toggleSemidark,
  toggleSidebar,
  resetToggleSidebar,
} = themeConfigSlice.actions;

export default themeConfigSlice.reducer;
